import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  FiChevronRight,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
} from "react-icons/fi";

import { IoCalendarSharp } from "react-icons/io5";

import { fetchChecklistStats } from "../../../features/checks/checkActions";
import { DynamicIcon } from "../../../components/DynamicIcon";

/* ============================================================
   CHECKLIST COLOR MAP
   ============================================================ */

const checklistColorMap = {
  blue: {
    iconBg: "bg-blue-50 text-blue-600",
    barColor: "bg-blue-500",
  },

  slate: {
    iconBg: "bg-slate-50 text-slate-600",
    barColor: "bg-slate-500",
  },

  orange: {
    iconBg: "bg-orange-50 text-orange-600",
    barColor: "bg-orange-500",
  },

  cyan: {
    iconBg: "bg-cyan-50 text-cyan-600",
    barColor: "bg-cyan-500",
  },

  violet: {
    iconBg: "bg-violet-50 text-violet-600",
    barColor: "bg-violet-500",
  },

  indigo: {
    iconBg: "bg-indigo-50 text-indigo-600",
    barColor: "bg-indigo-500",
  },

  red: {
    iconBg: "bg-red-50 text-red-600",
    barColor: "bg-red-500",
  },

  emerald: {
    iconBg: "bg-emerald-50 text-emerald-600",
    barColor: "bg-emerald-500",
  },

  amber: {
    iconBg: "bg-amber-50 text-amber-600",
    barColor: "bg-amber-500",
  },

  yellow: {
    iconBg: "bg-yellow-50 text-yellow-600",
    barColor: "bg-yellow-500",
  },
};

/* ============================================================
   DEFAULT CHECKLIST COLOR
   ============================================================ */

const defaultChecklistColor = {
  iconBg: "bg-indigo-50 text-indigo-600",
  barColor: "bg-indigo-500",
};

/* ============================================================
   COMPONENT
   ============================================================ */

const OperationalChecks = ({
  selectedDate,
  setSelectedDate,
}) => {
  const [activeTab, setActiveTab] = useState("All");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState("all");

  const dateInputRef = useRef(null);

  /* ============================================================
     FORMATTED DATE
     ============================================================ */

  const formattedDate = selectedDate
    ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(
        "en-GB",
        {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        }
      )
    : "Today";

  /* ============================================================
     OPEN DATE PICKER
     ============================================================ */

  const openDatePicker = () => {
    if (dateInputRef.current?.showPicker) {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current?.click();
    }
  };

  /* ============================================================
     FETCH STATS
     ============================================================ */

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);

const result = await dispatch(
          fetchChecklistStats(selectedDate)
        ).unwrap();

setStats(result?.data || null);
      } catch (error) {
        console.error(
          "Failed to fetch operational check stats:",
          error
        );

        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [dispatch, selectedDate]);

  /* ============================================================
     CHECKLIST STATS FROM API
     ============================================================ */

  const checklistStats = useMemo(() => stats?.checklists || [], [stats]);

  /* ============================================================
     FORMAT API DATA
     ============================================================ */

  const checkItems = useMemo(() => {
    return checklistStats.map((checklist) => {
      const totalTasks = checklist.totalTasks || 0;
      const completedTasks =
        checklist.completedTasks || 0;
      const actionRequired =
        checklist.actionRequired || 0;

      const progress =
        totalTasks > 0
          ? Math.round(
              (completedTasks / totalTasks) * 100
            )
          : 0;

      /* ========================================================
         DYNAMIC CHECKLIST COLOR
         ======================================================== */

      const colorConfig =
        checklistColorMap[checklist.color] ||
        defaultChecklistColor;

      /* ========================================================
         STATUS
         ======================================================== */

      let statusText = "Not Started";

      let statusStyle =
        "bg-slate-50 text-slate-600 border-slate-200";

      let StatusIcon = FiClock;

      if (
        totalTasks > 0 &&
        completedTasks === totalTasks
      ) {
        statusText = "Completed";

        statusStyle =
          "bg-emerald-50 text-emerald-700 border-emerald-100";

        StatusIcon = FiCheckCircle;
      } else if (actionRequired > 0) {
        statusText = "Action Required";

        statusStyle =
          "bg-amber-50 text-amber-700 border-amber-100";

        StatusIcon = FiAlertCircle;
      } else if (completedTasks > 0 || (checklist.slug === "delivery-check" && checklist.deliveryCount > 0)) {
        statusText = "In Progress";

        statusStyle =
          "bg-blue-50 text-blue-700 border-blue-100";

        StatusIcon = FiClock;
      }

      /* ========================================================
         NEXT ACTION
         ======================================================== */

      let actionText = "Start Check";

      if (
        totalTasks > 0 &&
        completedTasks === totalTasks
      ) {
        actionText = "View Check";
      } else if (actionRequired > 0) {
        actionText = "Review";
      } else if (completedTasks > 0 || (checklist.slug === "delivery-check" && checklist.deliveryCount > 0)) {
        actionText = "Continue";
      }

      /* ========================================================
         ROUTE
         ======================================================== */

      const route = checklist.slug
        ? `/checks/${checklist.slug}?date=${selectedDate}`
        : null;

      /* ========================================================
         RETURN
         ======================================================== */

      return {
        id: checklist.id,

        name: checklist.name,

        slug: checklist.slug,
        frequency: checklist.frequency,
        requiredToday: checklist.requiredToday,

        /* Dynamic values from API */
        icon: checklist.icon,

        color: checklist.color,

        totalTasks,

        completedTasks,

        actionRequired,

        progress,

        title: checklist.name,

        desc: checklist.frequency === "DAILY" ? "Daily" : "As Required",

        /* Dynamic styling */
        iconBg: colorConfig.iconBg,

        barColor: colorConfig.barColor,

        route,

        actionText,

        statusText,

        statusStyle,

        statusIcon: (
          <StatusIcon className="text-xs" />
        ),
      };
    });
  }, [checklistStats, selectedDate]);

  /* ============================================================
     FILTER CHECKLISTS
     ============================================================ */

  const categoryItems = useMemo(() => checkItems.filter(item =>
    viewType === 'all' || item.frequency === viewType
  ), [checkItems, viewType]);

  const filteredItems = useMemo(() => {
    switch (activeTab) {
      case "Due Today":
        return categoryItems.filter(
          (item) =>
            item.requiredToday && item.completedTasks < item.totalTasks
        );

      case "Not Required Today":
        return categoryItems.filter(item => !item.requiredToday);

      case "In Progress":
        return categoryItems.filter(
          (item) =>
            item.completedTasks > 0 &&
            item.requiredToday && item.completedTasks < item.totalTasks
        );

      case "Action Required":
        return categoryItems.filter(
          (item) => item.actionRequired > 0
        );

      case "Completed":
        return categoryItems.filter(
          (item) =>
            item.totalTasks > 0 &&
            item.completedTasks === item.totalTasks
        );

      case "All":
      default:
        return categoryItems;
    }
  }, [activeTab, categoryItems]);

  /* ============================================================
     TAB COUNTS
     ============================================================ */

  const tabCounts = useMemo(() => {
    const dueToday = categoryItems.filter(
      (item) =>
        item.requiredToday && item.completedTasks < item.totalTasks
    ).length;

    const inProgress = categoryItems.filter(
      (item) =>
        item.completedTasks > 0 &&
        item.requiredToday && item.completedTasks < item.totalTasks
    ).length;

    const completed = categoryItems.filter(
      (item) =>
        item.totalTasks > 0 &&
        item.completedTasks === item.totalTasks
    ).length;

    return {
      notRequiredToday: categoryItems.filter(item => !item.requiredToday).length,
      dueToday,
      inProgress,
      completed,
    };
  }, [categoryItems]);

  /* ============================================================
     FILTER TABS
     ============================================================ */

  const tabs = [
    {
      name: "All",
      count: null,
    },
    {
      name: "Due Today",
      count: tabCounts.dueToday,
      badgeBg:
        "bg-orange-100 text-orange-700",
    },
    {
      name: "In Progress",
      count: tabCounts.inProgress,
      badgeBg:
        "bg-amber-100 text-amber-700",
    },
    {
      name: "Completed",
      count: tabCounts.completed,
      badgeBg:
        "bg-emerald-100 text-emerald-700",
    },
    {
      name: "Not Required Today",
      count: tabCounts.notRequiredToday,
      badgeBg:
        "bg-slate-100 text-slate-700",
    },
  ];

  /* ============================================================
     NAVIGATION
     ============================================================ */

  const handleNavigation = (routePath) => {
    if (routePath) {
      navigate(routePath);
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div className="w-full">

      {/* ========================================================
          FILTER PILL BAR
      ======================================================== */}

      <div className="bg-white border border-slate-100 rounded-full p-1.5 shadow-sm flex flex-wrap items-center gap-1">
        {tabs.map((tab) => {
          const isActive =
            activeTab === tab.name;

          return (
            <button
              key={tab.name}
              onClick={() =>
                setActiveTab(tab.name)
              }
              className={`
                flex items-center gap-1.5
                px-4 py-1.5
                rounded-full
                text-xs font-medium
                transition-all
                ${
                  isActive
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50"
                }
              `}
            >
              <span>{tab.name}</span>

              {tab.count !== null && (
                <span
                  className={`
                    w-5 h-5 rounded-full
                    flex items-center justify-center
                    text-[10px] font-bold
                    ${
                      isActive
                        ? "bg-white/20 text-white"
                        : tab.badgeBg
                    }
                  `}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ========================================================
          OPERATIONAL CHECKS
      ======================================================== */}

      <div className="w-full bg-white border border-slate-100 rounded-2xl p-6 shadow-sm mt-4">

        {/* HEADER */}

        <div className="flex items-center justify-between gap-3 mb-6">

          <div className="flex items-center gap-3">
            <h3 className="text-base font-bold text-slate-900">
              Operational Checks
            </h3>

            <span className="bg-indigo-50 text-indigo-700 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
              {categoryItems.length} checks
            </span>
          </div>

          {/* RIGHT ACTIONS */}

          <div className="flex items-center gap-3">

            {/* VIEW TYPE */}

            <div className="relative">
              <select
                aria-label="Check category"
                value={viewType}
                onChange={(e) =>
                  { setViewType(e.target.value); setActiveTab("All"); }
                }
                className="appearance-none border border-slate-200 rounded-lg px-3 py-2 pr-8 text-xs font-medium text-slate-800 bg-white cursor-pointer shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200"
              >
                <option value="all">All Checks</option>
                <option value="DAILY">Daily</option>
                <option value="AS_REQUIRED">As Required</option>
              </select>

              <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
            </div>

            {/* DATE SELECTOR */}

            <button
              type="button"
              onClick={openDatePicker}
              className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-slate-800 bg-white cursor-pointer shadow-sm hover:bg-slate-50"
            >
              <IoCalendarSharp className="text-slate-700 text-sm" />

              <span>
                {selectedDate
                  ? formattedDate
                  : "Select Date"}
              </span>

              <FiChevronDown className="text-slate-400 text-sm ml-1" />
            </button>

            {/* HIDDEN DATE INPUT */}

            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate || ""}
              onChange={(e) =>
                setSelectedDate(e.target.value)
              }
              className="absolute opacity-0 pointer-events-none"
            />

          </div>
        </div>

        {/* ======================================================
            EMPTY STATE
        ======================================================= */}

        {!loading &&
          filteredItems.length === 0 && (
            <div className="py-10 text-center text-xs text-slate-400">
              No checks found.
            </div>
          )}

        {/* ======================================================
            TABLE
        ======================================================= */}

        {!loading &&
          filteredItems.length > 0 && (
            <div className="w-full overflow-x-auto">

              <table className="w-full min-w-[800px] text-left border-collapse">

                {/* TABLE HEADER */}

                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-[11px] font-semibold tracking-wider">

                    <th className="pb-3 w-[35%] font-medium">
                      Check
                    </th>

                    <th className="pb-3 w-[25%] font-medium">
                      Tasks Today
                    </th>

                    <th className="pb-3 w-[20%] font-medium">
                      Status
                    </th>

                    <th className="pb-3 w-[20%] text-right font-medium pr-4">
                      Next Action
                    </th>

                  </tr>
                </thead>

                {/* TABLE BODY */}

                <tbody className="divide-y divide-slate-50">

                  {filteredItems.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() =>
                        handleNavigation(item.route)
                      }
                      className="hover:bg-slate-50/50 transition-colors group hover:cursor-pointer"
                    >

                      {/* ==================================================
                          CHECK
                      =================================================== */}

                      <td className="py-3.5 pr-4">

                        <div className="flex items-start gap-3">

                          {/* DYNAMIC ICON */}

                          <div
                            className={`
                              w-9 h-9 rounded-lg
                              flex items-center justify-center
                              shrink-0
                              ${item.iconBg}
                            `}
                          >
                            <DynamicIcon
                              iconName={item.icon}
                              className="w-4 h-4"
                            />
                          </div>

                          <div>
                            <h4 className="text-xs font-bold text-slate-800 leading-snug">
                              {item.title}
                            </h4>

                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {item.desc}
                            </p>
                          </div>

                        </div>

                      </td>

                      {/* ==================================================
                          TASKS
                      =================================================== */}

                      <td className="py-3.5 pr-6">

                        <span className="text-xs font-semibold text-slate-700 block">
                          {item.completedTasks} /{" "}
                          {item.totalTasks}
                        </span>

                        <span className="text-[11px] text-slate-400 block mt-1">
                          {item.completedTasks} completed
                        </span>

                        {/* DYNAMIC PROGRESS COLOR */}

                        <div className="w-full max-w-[160px] bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">

                          <div
                            className={`
                              h-full
                              rounded-full
                              ${item.barColor}
                            `}
                            style={{
                              width: `${item.progress}%`,
                            }}
                          />

                        </div>

                      </td>

                      {/* ==================================================
                          STATUS
                      =================================================== */}

                      <td className="py-3.5 pr-4">

                        <div
                          className={`
                            inline-flex items-center gap-1
                            px-2 py-1 rounded-md
                            border text-[11px] font-medium
                            ${item.statusStyle}
                          `}
                        >
                          {item.statusIcon}

                          <span>
                            {item.statusText}
                          </span>
                        </div>

                      </td>

                      {/* ==================================================
                          ACTION
                      =================================================== */}

                      <td className="py-3.5 text-right pr-2">

                        <div className="flex items-center justify-end gap-2">

                          <button
                            disabled={!item.route}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigation(
                                item.route
                              );
                            }}
                            className={`
                              border border-slate-200
                              bg-white
                              font-medium text-xs
                              py-1.5 px-4
                              rounded-lg
                              shadow-sm
                              transition-all
                              ${
                                item.route
                                  ? "hover:border-indigo-600 hover:text-indigo-600 text-slate-700 cursor-pointer"
                                  : "text-slate-400 opacity-60 cursor-not-allowed"
                              }
                            `}
                          >
                            {item.actionText}
                          </button>

                          <FiChevronRight
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigation(
                                item.route
                              );
                            }}
                            className={`
                              text-base transition-colors
                              ${
                                item.route
                                  ? "text-slate-300 group-hover:text-slate-500 cursor-pointer"
                                  : "text-slate-200 cursor-not-allowed"
                              }
                            `}
                          />

                        </div>

                      </td>

                    </tr>
                  ))}

                </tbody>

              </table>

            </div>
          )}
      </div>
    </div>
  );
};

export default OperationalChecks;